import React, { useEffect } from 'react';
import { LoadingSpinner } from './components/LoadingSpinner';
import { financialAidApiService } from './api/financialAidApiService';
import { FinancialAidProgram } from './types';
import { exportFinancialAidToExcel } from './utils/excelExport';

interface FinancialAidProps {
  onClose?: () => void;
}

const FinancialAid: React.FC<FinancialAidProps> = ({ onClose }) => {
  const [selectedCategory, setSelectedCategory] = React.useState<string>("all");
  const [isLoading, setIsLoading] = React.useState(true);
  const [programs, setPrograms] = React.useState<FinancialAidProgram[]>([]);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  // Load financial aid data on component mount
  useEffect(() => {
    const loadFinancialAidData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load programs and categories in parallel
        const [programsResponse, categoriesResponse] = await Promise.all([
          financialAidApiService.getFinancialAidPrograms({ limit: 100, active_only: true }),
          financialAidApiService.getFinancialAidCategories()
        ]);

        setPrograms(programsResponse.data);
        setCategories(['all', ...categoriesResponse.categories]);
      } catch (err) {
        console.error('Failed to load financial aid data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load financial aid data');
      } finally {
        setIsLoading(false);
      }
    };

    loadFinancialAidData();
  }, []);

  // Filter programs based on selected category
  const filteredPrograms = selectedCategory === "all"
    ? programs
    : programs.filter(program => program.category === selectedCategory);

  // Group programs by category
  const groupedPrograms = filteredPrograms.reduce((acc, program) => {
    if (!acc[program.category]) {
      acc[program.category] = [];
    }
    acc[program.category].push(program);
    return acc;
  }, {} as Record<string, FinancialAidProgram[]>);

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'grant':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'scholarship':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'loan':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'work-study':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'last-dollar scholarship':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case '175+ scholarships':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'info / fafsa code':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cost sheet':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Handle backdrop click to close modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 ease-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary to-primary-dark">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-[#1b2c5a]">Financial Aid Resources</h2>
            <p className="text-[#1b2c5a] text-opacity-70 text-xs mt-0.5">
              Explore available financial aid options
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Download Excel Button */}
            {programs.length > 0 && (
              <button
                onClick={() => exportFinancialAidToExcel(programs)}
                className="px-6 py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
                title="Download Excel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Excel
              </button>
            )}
            {/* Close Button */}
            <button
              onClick={onClose}
              className="px-6 py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
              title="Close Modal"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {isLoading ? (
            <LoadingSpinner 
              message="Loading financial aid resources..." 
              size="md" 
              color="primary" 
            />
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">Error Loading Data</h3>
                <p className="text-sm">{error}</p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Try Again
              </button>
            </div>
                      ) : (
              <>
                {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-6">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category
                        ? 'bg-primary text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                    }`}
                  >
                    {category === "all" ? "All Categories" : category}
                  </button>
                ))}
              </div>

              {/* Financial Aid Cards */}
              <div className="space-y-6">
                {Object.entries(groupedPrograms).map(([category, items]) => (
                  <div key={category} className="space-y-4">
                    <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">
                      {category}
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {items.map((program) => (
                        <div key={program.program_id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 text-sm leading-tight mb-1">
                                {program.program_name || program.title}
                              </h4>
                              {program.provider && (
                                <p className="text-xs text-gray-600 font-medium">
                                  {program.provider}
                                </p>
                              )}
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(program.type)} ml-2 flex-shrink-0`}>
                              {program.type}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                            {program.description}
                          </p>
                          {program.website && (
                            <a
                              href={program.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-primary hover:text-primary-dark text-sm font-medium transition-colors"
                            >
                              Learn More
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* No programs found */}
              {filteredPrograms.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">No Programs Found</h3>
                    <p className="text-sm">No financial aid programs match your current filter.</p>
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Bottom Download Button */}
          {programs.length > 0 && (
            <div className="mt-6 p-4 border-t border-[#e6e6e6] bg-[#f8f9fa] rounded-b-xl">
              <div className="flex justify-center">
                <button
                  onClick={() => exportFinancialAidToExcel(programs)}
                  className="px-8 py-3 bg-[#28a745] text-[#ffffff] rounded-xl hover:bg-[#218838] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
                  title="Download Excel"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Excel Report
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialAid; 